import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAlertWebSocket } from './WebSocketContext';
import { fetchActiveAlerts } from '../services/alertService';
import { hasDomain, hasValidAuth } from '../utils/authStorage';

const ActiveAlertsContext = createContext(null);

export function ActiveAlertsProvider({ children }) {
  const { subscribe } = useAlertWebSocket();
  const [activeAlerts, setActiveAlerts] = useState([]);

  const refresh = useCallback(async () => {
    if (!hasValidAuth() || !hasDomain('surveillance')) {
      setActiveAlerts([]);
      return;
    }
    try {
      const data = await fetchActiveAlerts();
      setActiveAlerts(data);
    } catch (err) {
      console.error("Failed to fetch active alerts", err);
    }
  }, []);

  useEffect(() => {
    if (!hasDomain('surveillance')) return;
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!hasDomain('surveillance')) return;
    const unsub = subscribe((raw) => {
      const type = raw?.type;
      const data = raw?.message || raw?.payload || raw?.data;

      if (type === "alert.create") {
        if (data) {
          setActiveAlerts((prev) => {
            if (prev.some((a) => a.id === data.id)) return prev;
            return [data, ...prev].sort(
              (a, b) => new Date(b.start_time || b.timestamp) - new Date(a.start_time || a.timestamp)
            );
          });
        }
      } else if (type === "alert.complete") {
        const id = data?.alert_id || data?.id;
        if (id) {
          setActiveAlerts((prev) => prev.filter((a) => a.id !== id));
        }
      } else if (
        type === "drone.alert" ||
        type === "drone.complete" ||
        type === "alert.verify" ||
        type === "alert.update"
      ) {
        refresh();
      }
    });
    return unsub;
  }, [subscribe, refresh]);

  return (
    <ActiveAlertsContext.Provider value={{ activeAlerts, refresh }}>
      {children}
    </ActiveAlertsContext.Provider>
  );
}

export function useActiveAlerts() {
  const ctx = useContext(ActiveAlertsContext);
  if (!ctx) {
    throw new Error("useActiveAlerts must be used within ActiveAlertsProvider");
  }
  return ctx;
}
